import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pill, Pencil, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

export function MedicationsTab({ clientId }) {
  const blank = { name: "", dosage: "", frequency: "AM Only", route: "Oral", purpose: "", is_prn: false };
  const [meds, setMeds] = useState([]);
  const [draftMed, setDraftMed] = useState(blank);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    if (clientId) load();
  }, [clientId]);

  async function load() {
    try {
      setLoading(true);
      const res = await api.get(`/api/medications?client_id=${clientId}`);
      setMeds(res.data);
    } catch (err) {
      toast.error(formatApiError(err, "Failed to load medications"));
    } finally {
      setLoading(false);
    }
  }

  async function handleAdd() {
    if (!draftMed.name.trim()) return toast.error("Medication name required");
    try {
      const res = await api.post("/api/medications", { ...draftMed, client_id: clientId });
      setMeds([...meds, res.data]);
      setDraftMed(blank);
      toast.success("Medication added");
    } catch (err) {
      toast.error(formatApiError(err, "Failed to add medication"));
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this medication?")) return;
    try {
      await api.delete(`/api/medications/${id}`);
      setMeds(meds.filter((m) => m.id !== id));
      toast.success("Medication removed");
    } catch (err) {
      toast.error(formatApiError(err, "Failed to delete medication"));
    }
  }

  async function handleSaveEdit(id, updatedFields) {
    try {
      const res = await api.put(`/api/medications/${id}`, updatedFields);
      setMeds(meds.map((m) => (m.id === id ? res.data : m)));
      setEditingId(null);
      toast.success("Medication updated");
    } catch (err) {
      toast.error(formatApiError(err, "Failed to update medication"));
    }
  }

  if (loading) return <div className="p-4 text-sm text-muted-foreground">Loading medications...</div>;

  return (
    <Card className="p-6 space-y-6">
      <div>
        <h3 className="text-lg font-medium text-title mb-1">Medications</h3>
        <p className="text-sm text-muted-foreground">Manage active prescriptions, schedules, and administration details.</p>
      </div>

      <div className="space-y-4">
        {meds.map((med) => {
          const isEditing = editingId === med.id;
          return (
            <Card key={med.id} className="p-4 border border-border bg-card/50 relative group">
              {isEditing ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Medication Name</Label>
                    <Input defaultValue={med.name} id={`edit-name-${med.id}`} />
                  </div>
                  <div>
                    <Label>Dosage</Label>
                    <Input defaultValue={med.dosage} id={`edit-dosage-${med.id}`} />
                  </div>
                  <div>
                    <Label>Frequency</Label>
                    <Select defaultValue={med.frequency} id={`edit-freq-${med.id}`}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["AM Only", "PM Only", "Both (AM & PM)", "PRN (As Needed)"].map((f) => (
                          <SelectItem key={f} value={f}>{f}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Route</Label>
                    <Select defaultValue={med.route} id={`edit-route-${med.id}`}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["Oral", "Topical", "Injection", "Inhalation", "Ophthalmic", "Otic"].map((r) => (
                          <SelectItem key={r} value={r}>{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label>Medical Purpose</Label>
                    <Input defaultValue={med.purpose || ""} id={`edit-purpose-${med.id}`} />
                  </div>
                  <div className="md:col-span-2 flex justify-end gap-2 pt-2">
                    <Button variant="outline" size="sm" onClick={() => setEditingId(null)}>Cancel</Button>
                    <Button size="sm" onClick={() => {
                      const name = document.getElementById(`edit-name-${med.id}`).value;
                      const dosage = document.getElementById(`edit-dosage-${med.id}`).value;
                      const purpose = document.getElementById(`edit-purpose-${med.id}`).value;
                      handleSaveEdit(med.id, { name, dosage, purpose });
                    }}><Save className="h-4 w-4 mr-1" /> Save</Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between">
                  <div className="flex gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg text-primary mt-0.5"><Pill className="h-4 w-4" /></div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-text-main">{med.name}</h4>
                        <span className="text-sm text-muted-foreground">— {med.dosage}</span>
                        {med.is_prn && <Badge variant="secondary" className="bg-amber-100 text-amber-800 border-none">PRN</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {med.frequency} · {med.route} {med.purpose ? `· ${med.purpose}` : "· no medical purpose recorded"}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-text-main" onClick={() => setEditingId(med.id)}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => handleDelete(med.id)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              )}
            </Card>
          );
        })}

        {meds.length === 0 && <div className="text-center py-6 border border-dashed rounded-lg text-muted-foreground text-sm">No medications recorded yet.</div>}
      </div>

      <div className="pt-4 border-t border-border space-y-4">
        <h4 className="text-xs font-semibold text-muted-foreground tracking-wider uppercase">Add Medication</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label className="text-label">Medication Name</Label>
            <Input value={draftMed.name} onChange={(e) => setDraftMed({ ...draftMed, name: e.target.value })} placeholder="e.g., Lisinopril" />
          </div>
          <div>
            <Label className="text-label">Dosage</Label>
            <Input value={draftMed.dosage} onChange={(e) => setDraftMed({ ...draftMed, dosage: e.target.value })} placeholder="e.g., 500mg" />
          </div>
          <div>
            <Label className="text-label">Frequency</Label>
            <Select value={draftMed.frequency} onValueChange={(v) => setDraftMed({ ...draftMed, frequency: v, is_prn: v === "PRN (As Needed)" })}>
              <SelectTrigger><SelectValue placeholder="Select frequency" /></SelectTrigger>
              <SelectContent>
                {["AM Only", "PM Only", "Both (AM & PM)", "PRN (As Needed)"].map((f) => (
                  <SelectItem key={f} value={f}>{f}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-label">Route</Label>
            <Select value={draftMed.route} onValueChange={(v) => setDraftMed({ ...draftMed, route: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Oral", "Topical", "Injection", "Inhalation", "Ophthalmic", "Otic"].map((r) => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-label">Medical Purpose</Label>
            <Input value={draftMed.purpose} onChange={(e) => setDraftMed({ ...draftMed, purpose: e.target.value })} placeholder="e.g., hypertension, anxiety, seizure control..." />
          </div>
        </div>
        <Button onClick={handleAdd} className="mt-2 bg-primary text-primary-foreground"><Pill className="h-4 w-4 mr-2" /> Add</Button>
      </div>
    </Card>
  );
}
